import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import clsx from 'clsx'
import TaskItem from '../TaskItem'
import AttachmentsModal from 'components/QuestionnaireRequestAutomated/processor/modal/attachmentsModal'
import { TasksSectionContext } from 'components/QuestionnaireRequestAutomated/processor/components/TasksSection'
import { useStoreActions } from 'components/utility/sandbox_upload/uploadStore'
import { UPLOAD_STAGES } from 'components/utility/sandbox_upload/constants'
import { addRespondingFile } from 'components/QuestionnaireRequestAutomated/processor/slices/globalSlice'

// Files attach in parallel as they finish processing, so the server orders them by selection position instead.
// Gaps leave room to reorder later by placing an attachment between its neighbours.
const POSITION_GAP = 100
const nextPosition = (last) => last + POSITION_GAP
const maxPosition = (attachments) => Math.max(0, ...Object.values(attachments || {}).map((a) => a.position ?? 0))

export default function UploadTask({ disabled = false }) {
  const dispatch = useDispatch()
  const [openAttachmentsModal, setOpenAttachmentsModal] = useState(false)
  const respondedFiles = useSelector((state) => state.global.respondingFiles)
  const workflowType = useSelector((state) => state.global.workflowType)
  const hashedId = useSelector((state) => state.global.hashedId)
  const { initialAttachments } = useContext(TasksSectionContext)
  const positionsRef = useRef({})
  const lastPositionRef = useRef(maxPosition(initialAttachments))
  const attachedRef = useRef(new Set())

  useStoreActions(useCallback((action) => {
    if (action.payload.uploaderUUID !== hashedId) return

    const { fileUUID } = action.payload
    if (action.type === 'ADD_FILE') {
      lastPositionRef.current = nextPosition(lastPositionRef.current)
      positionsRef.current[fileUUID] = lastPositionRef.current
    }
    if (
      action.type === 'UPDATE_STATUS' &&
      action.payload.stage === UPLOAD_STAGES.COMPLETED &&
      !attachedRef.current.has(fileUUID)
    ) {
      attachedRef.current.add(fileUUID)
      dispatch(
        addRespondingFile({
          questionnaireRequestId: hashedId,
          fileSandboxId: fileUUID,
          position: positionsRef.current[fileUUID],
        })
      )
    }
  }, [hashedId, dispatch]))

  const labelAttributes = useMemo(() => {
    const title = workflowType === 'automated' ? 'attachments' : 'upload'
    const numberOfFiles = respondedFiles.length
    const subTitle = numberOfFiles === 0 ? 'no attachments' : `${numberOfFiles} ${numberOfFiles === 1 ? 'attachment' : 'attachments'}`
    const btnTitle = workflowType === 'manual' && numberOfFiles === 0 ? 'start' : 'open'
    const submitBtnClasses = clsx(
      'dome-btn dome-btn-base',
      workflowType === 'automated' || numberOfFiles > 0 ? ' dome-btn-grey-stroke' : 'dome-btn-go-green'
    )

    return { title, subTitle, btnTitle, submitBtnClasses }
  }, [workflowType, respondedFiles.length])

  const modalAttributes = useMemo(() => {
    const headerLabel = 'attach files'

    return { headerLabel }
  }, [workflowType])

  return (
    <>
      <TaskItem
        disabled={disabled}
        title={labelAttributes.title}
        subTitle={labelAttributes.subTitle}
        btnTitle={labelAttributes.btnTitle}
        iconClassName="fa-light fa-paperclip"
        onSubmit={() => setOpenAttachmentsModal(true)}
        submitBtnClasses={labelAttributes.submitBtnClasses}
      />
      <AttachmentsModal
        open={openAttachmentsModal}
        onClose={() => setOpenAttachmentsModal(false)}
        initialAttachments={initialAttachments}
        headerLabel={modalAttributes.headerLabel}
      />
    </>
  )
}
